"use client";

import { CatalogProductCard } from "@/features/catalog";
import type { CatalogProduct } from "@/features/catalog";
import { Link, getProductUrl } from "@/shared";
import { useViewportTracking } from "@dafiti/analytics-sdk";
import { useTranslations } from "next-intl";
import type { MouseEvent } from "react";

import { useRemoveFromWishlist } from "../../hooks/useRemoveFromWishlist.hook";
import * as track from "../../tracking/wishlist.tracking";
import type { WishlistProduct } from "../../types/wishlist.type";
import { AddToWishlistButton } from "../AddToWishlistButton/AddToWishlistButton";

type Props = {
  product: WishlistProduct;
  index?: number;
};

export function WishlistProductCard({ product, index = 1 }: Props) {
  const t = useTranslations("wishlist");
  const { mutate: removeFromWishlist, isPending } = useRemoveFromWishlist();

  const { ref } = useViewportTracking(
    product.sku,
    { ...product, index },
    { threshold: 0.5 },
  );

  const catalogProduct = {
    ...product,
    images:
      product.images.length > 0
        ? product.images
        : [{ url: "https://broken.invalid/image.jpg" }],
  } as unknown as CatalogProduct;

  function stopLinkNavigation(e: MouseEvent<HTMLElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleRemoveClick() {
    if (isPending) return;

    track.removeFromWishlistClick({ product: product as never });

    removeFromWishlist({ sku: product.sku, productName: product.name });
  }

  function handleProductClick() {
    track.wishlistProductClick({ ...product, index }, index);
  }

  return (
    <div ref={ref}>
      <Link
        useRouterLink
        href={getProductUrl(product.url) || "/"}
        onClick={handleProductClick}
      >
        <CatalogProductCard
          product={catalogProduct}
          disableTracking
          disableViewportTracking
          cardClassName={
            !product.is_available ? "opacity-50 grayscale" : undefined
          }
          imageOverlaySlot={
            <>
              <div
                className="pointer-events-auto absolute right-2 top-2 z-10"
                onClick={stopLinkNavigation}
              >
                <AddToWishlistButton
                  variant="icon"
                  filled
                  isWishlisted
                  onClick={handleRemoveClick}
                  data-testid="wishlist-card-remove-button"
                />
              </div>
              {!product.is_available && (
                <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-3">
                  <span className="rounded bg-white/90 px-2 py-0.5 text-2xs font-medium text-gray-600">
                    {t("card.unavailable")}
                  </span>
                </div>
              )}
            </>
          }
        />
      </Link>
    </div>
  );
}
